// Small inline editors shared by the Timeline designer.
import { useEffect, useState } from "react";
import { Check, ChevronDown, Info } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

export const ROLE_LABEL = { case_legal_handler: "Legal owner", case_handler: "Case handler", legal_head: "Head of Legal" };
export const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);

export function EditableText({ value, onChange, disabled, placeholder, className = "", multiline = false, testid, numeric = false, min, max }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");
  useEffect(() => { if (!editing) setV(value ?? ""); }, [value, editing]);
  const commit = () => { setEditing(false); const out = numeric ? (v === "" ? null : Number(v)) : v; if (out !== value) onChange(out); };
  const base = numeric ? "inline-block" : "block w-full";
  if (disabled) return <span className={`${base} ${className} ${value == null || value === "" ? "text-slate-300" : ""}`}>{value ?? placeholder ?? "—"}</span>;
  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)} className={`${base} text-left rounded px-1 -mx-1 hover:bg-amber-50 hover:ring-1 hover:ring-amber-200 ${className} ${value == null || value === "" ? "text-slate-300 italic" : ""}`} data-testid={testid} title="Click to edit">
        {value === "" || value == null ? (placeholder || "—") : String(value)}
      </button>
    );
  }
  const common = { autoFocus: true, value: v, onChange: (e) => setV(e.target.value), onBlur: commit, onKeyDown: (e) => { if (e.key === "Enter" && !multiline) commit(); if (e.key === "Escape") { setV(value ?? ""); setEditing(false); } }, className: `${numeric ? "" : "w-full"} border border-amber-300 rounded px-1 py-0.5 text-xs bg-white outline-none ${className}`, "data-testid": `${testid}-input` };
  return multiline ? <textarea rows={3} {...common} /> : <input type={numeric ? "number" : "text"} min={min} max={max} {...common} />;
}

export function YesNo({ value, onChange, disabled, testid }) {
  return <span className="inline-flex items-center gap-1.5"><Switch checked={!!value} onCheckedChange={onChange} disabled={disabled} className="scale-75 origin-left" data-testid={testid} /><span className={`text-[11px] ${value ? "text-emerald-700 font-medium" : "text-slate-400"}`}>{value ? "Yes" : "No"}</span></span>;
}

export function ChipMultiSelect({ value = [], options, onChange, disabled, placeholder = "— none —", testid, labelOf }) {
  const [open, setOpen] = useState(false);
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button type="button" className={`flex flex-wrap gap-1 min-h-[22px] w-full text-left rounded px-1 -mx-1 ${disabled ? "" : "hover:bg-amber-50 hover:ring-1 hover:ring-amber-200"}`} data-testid={testid}>
          {value.length === 0 && <span className="text-slate-300 italic text-[11px]">{placeholder}</span>}
          {value.map((k) => <span key={k} className="text-[10px] px-1.5 py-px rounded-full border bg-rose-50 text-rose-800 border-rose-200 whitespace-nowrap">{labelOf ? labelOf(k) : (options.find((o) => o.key === k)?.label || k)}</span>)}
          {!disabled && <ChevronDown className="w-3 h-3 text-slate-400 ml-auto self-center" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1 max-h-72 overflow-y-auto text-xs" data-testid={`${testid}-menu`}>
        {options.map((o) => (
          <label key={o.key} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-100 cursor-pointer">
            <Checkbox checked={value.includes(o.key)} onCheckedChange={() => toggle(o.key)} data-testid={`${testid}-opt-${o.key}`} />
            <span className="truncate">{o.label}</span>{o.hint && <span className="ml-auto text-[9px] text-slate-400">{o.hint}</span>}
          </label>
        ))}
        {!options.length && <div className="px-2 py-1 text-slate-400">Nothing to pick</div>}
      </PopoverContent>
    </Popover>
  );
}

export function EnumPick({ value, options, onChange, disabled, testid, className = "" }) {
  if (disabled) return <span className={className}>{options.find((o) => o.value === value)?.label || value || "—"}</span>;
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`h-6 border rounded px-1 text-[11px] bg-white ${className}`} data-testid={testid}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function Tick({ on }) { return on ? <Check className="w-3 h-3 text-emerald-600 inline" /> : <span className="text-slate-300">—</span>; }

// ⓘ info card — the same wording in the designer's headers / boxes and the map's step hover
export function InfoHover({ help, testid, className = "", side = "bottom", children }) {
  if (!help) return children || null;
  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button type="button" className={`inline text-left [text-transform:inherit] [letter-spacing:inherit] [font-size:inherit] [font-weight:inherit] text-inherit group ${className}`} aria-label={`What is ${help.title}?`} data-testid={testid}>
          {children}<span className="whitespace-nowrap">{"\u2060"}<Info className="w-3 h-3 inline-block align-[-2px] ml-0.5 text-slate-400 group-hover:text-slate-700" /></span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent side={side} align="start" className="w-[340px] p-3 space-y-1.5 shadow-xl border-slate-200 normal-case tracking-normal whitespace-normal font-normal text-left" data-testid={testid ? `${testid}-card` : undefined}>
        <div className="text-xs font-semibold text-slate-900">{help.title}</div>
        {help.lines.map((l, i) => <p key={i} className="text-[11px] text-slate-700 leading-snug">{l}</p>)}
        {help.example && <div className="text-[11px] text-slate-800 bg-slate-50 border border-slate-200 rounded px-2 py-1 leading-snug"><span className="text-[9px] uppercase tracking-wider text-slate-400 mr-1">Example</span>{help.example}</div>}
      </HoverCardContent>
    </HoverCard>
  );
}
